import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { UserHelperService } from '@helpers/user-helper.service';
import { FileMessageUploadedI } from '@interfaces/file-message-upload.interface';
import { UserAttributesI } from '@interfaces/user.interface';
import { Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  private fileSlug: string = 'file-uploader/';
  private imageSlug: string = 'file-uploader/image';
  private audioSlug: string = 'audio-uploader/';
  constructor(private http: HttpClient, private readonly userHelperService: UserHelperService) {}

  // Size- and type-checked variant (png/jpeg/webp, 5MB) for images the product
  // renders back to users — the generic route above stays open because chat
  // attachments are legitimately any file type.
  public sendImageToFileService(fileBlob: File): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', fileBlob, this.generateRandomName(fileBlob.name));
    formData.append('fileName', fileBlob.name);

    return this.http.post(`${environment.OMNISMART_API_UPLOADER}${this.imageSlug}`, formData);
  }

  public sendFileToFileService(fileBlob: File): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders();
    const formData: FormData = new FormData();

    formData.append('file', fileBlob, this.generateRandomName(fileBlob.name));
    formData.append('fileName', fileBlob.name);

    return this.http.post(`${environment.OMNISMART_API_UPLOADER}${this.fileSlug}`, formData, {
      headers: headers,
    });
  }

  public sendFileMessageToFileService(fileBlob: any, chatData: FileMessageUploadedI): Observable<any> {
    const { chatId, customerId, fileName, contentEncoding } = chatData;
    let formData: FormData = new FormData();
    let headers = new HttpHeaders();
    formData.append('file', fileBlob, fileName);
    formData.append('chatId', chatId);
    formData.append('customerId', customerId);
    formData.append('fileName', fileName);

    if (contentEncoding) {
      formData.append('contentEncoding', contentEncoding);
    }

    return this.http.post(`${environment.OMNISMART_API_UPLOADER}${this.fileSlug}message`, formData, {
      headers: headers,
    });
  }

  public sendAudioMessageToFileService(fileBlob: any, chatData: FileMessageUploadedI): Observable<any> {
    const { chatId, customerId, fileName, contentEncoding } = chatData;
    let formData: FormData = new FormData();

    let headers = new HttpHeaders();
    formData.append('audio', fileBlob, fileName);
    formData.append('chatId', chatId);
    formData.append('customerId', customerId);
    formData.append('fileName', fileName);

    if (contentEncoding) {
      formData.append('contentEncoding', contentEncoding);
    }

    return this.http.post(`${environment.OMNISMART_API_UPLOADER}${this.audioSlug}message`, formData, {
      headers: headers,
    });
  }

  public sendFileUserProfile(fileBlob: File, user?: { customer: string; userId: string }): Observable<any> {
    const headers: HttpHeaders = new HttpHeaders();
    const formData: FormData = new FormData();

    const currentUser = <UserAttributesI>this.userHelperService.getUserAttributes();

    formData.append('customerId', user?.customer ?? currentUser['custom:customer'] ?? '');
    formData.append('userId', user?.userId ?? currentUser['custom:userId'] ?? '');
    formData.append('file', fileBlob);
    return this.http.post(`${environment.OMNISMART_API_UPLOADER}${this.fileSlug}user-profile`, formData, {
      headers: headers,
    });
  }

  // Use the last dot so names with extra dots (e.g. "report.2026.png") still yield a
  // clean "uuid.ext" key. split('.').length === 2 left such names untouched, uploading
  // them with spaces/commas that later broke the rendered url.
  private generateRandomName(fileName: string) {
    const lastDot = fileName.lastIndexOf('.');

    if (lastDot > 0) {
      return `${uuidv4()}${fileName.slice(lastDot)}`;
    }

    return fileName;
  }
}
